"use client";

import { Avatar, Button, Dropdown, Label } from "@heroui/react";
import { useClerk, useUser } from "@clerk/react";
import { useState } from "react";
import {
  UserCircle,
  Settings,
  LogOut,
  Building2,
  HelpCircle,
  ShieldUser,
} from "lucide-react";
import { ROLES } from "@/utils/roles";

const getInitials = (name) => {
  if (!name) return "AC";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const roleLabel = (role) => (role === ROLES.ADMIN ? "Admin" : "User");

const AccountDropdown = ({
  iconOnly = false,
}) => {
  const { signOut, openUserProfile } = useClerk();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);

  const fullName = user?.fullName ?? "Account";
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const initials = getInitials(user?.fullName);

  const handleAction = (key) => {
    switch (key) {
      case "my-profile":
        openUserProfile();
        break;
      case "sign-out":
        signOut({ redirectUrl: "/" });
        break;
      default:
        break;
    }
    setIsOpen(false);
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-200 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />

      <Dropdown isOpen={isOpen} onOpenChange={setIsOpen}>
        <Button
          isIconOnly={iconOnly}
          aria-label="Account menu"
          variant="ghost"
          className="relative z-40 h-auto ustify-start rounded-xl"
        >
          <Avatar
            size="sm"
            className="size-10 shrink-0 rounded-full border border-accent/40"
            color="accent"
            variant="soft"
          >
            <Avatar.Fallback className="rounded-full font-semibold">
              {initials}
            </Avatar.Fallback>
          </Avatar>

          {!iconOnly && (
            <div className="flex min-w-0 flex-col items-start">
              <span className="max-w-36 truncate text-sm font-medium">
                {fullName}
              </span>
              {/* <span className="text-xs text-muted">{roleLabel(currentRole)}</span> */}
            </div>
          )}
        </Button>

        <Dropdown.Popover className="min-w-72 overflow-hidden rounded-xl border border-separator p-0 shadow-xl">
          <div className="flex items-center gap-3 border-b border-separator bg-surface-secondary px-4 py-3">
            <Avatar size="md" className="size-10 shrink-0">
              <Avatar.Fallback className="rounded-full bg-accent font-semibold text-white">
                {initials}
              </Avatar.Fallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {fullName}
              </p>
              {email && <p className="truncate text-xs text-muted">{email}</p>}
            </div>
          </div>

          <Dropdown.Menu onAction={handleAction} className="p-1.5">
            <Dropdown.Item id="my-profile" textValue="My Profile" className="rounded-lg">
              <UserCircle className="h-4 w-4 shrink-0 text-muted" />
              <Label>My Profile</Label>
            </Dropdown.Item>

            <Dropdown.Item
              id="company-settings"
              textValue="Company Settings"
              className="rounded-lg"
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted" />
              <Label>Company Settings</Label>
            </Dropdown.Item>

            <Dropdown.Item id="preferences" textValue="Preferences" className="rounded-lg">
              <Settings className="h-4 w-4 shrink-0 text-muted" />
              <Label>Preferences</Label>
            </Dropdown.Item>

            <Dropdown.Item id="help" textValue="Help &amp; Support" className="rounded-lg">
              <HelpCircle className="h-4 w-4 shrink-0 text-muted" />
              <Label>Help &amp; Support</Label>
            </Dropdown.Item>

            {/* {canSwitch && (
              <Dropdown.Item
                id="space-switch"
                textValue={inAdminSpace ? "View User Space" : "View Admin Space"}
                className="rounded-lg border-t border-separator mt-1 pt-2"
              >
                <ShieldUser className="h-4 w-4 shrink-0 text-muted" />
                <Label>{inAdminSpace ? "View User Space" : "View Admin Space"}</Label>
              </Dropdown.Item>
            )} */}

            <Dropdown.Item
              id="sign-out"
              textValue="Sign out"
              variant="danger"
              className="group mt-1 rounded-lg border-t border-separator pt-2 hover:bg-danger/8"
            >
              <LogOut className="h-4 w-4 shrink-0 group-hover:text-danger" />
              <Label>Sign out</Label>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </>
  );
};

export default AccountDropdown;